// src/services/marketData/marketDataService.ts
import { ProviderFactory } from './providerFactory';
// yahooFinanceService import removed: using default provider (Alpaca) for market indices

// Define interfaces
export interface MarketIndex {
  name: string;
  symbol: string;
  value: number;
  changePercent: number;
}

export interface MarketSector {
  name: string;
  performance: number;
}

// Create a simple in-memory cache
class SimpleCache {
  private cache = new Map<string, { data: any, timestamp: number }>();
  private ttlMap = new Map<string, number>(); // Store TTL per key

  get(key: string): any {
    const item = this.cache.get(key);
    if (!item || Date.now() > item.timestamp) {
        this.delete(key); // Delete expired item
        return null;
    }
    return item.data;
  }

  set(key: string, data: any, ttlMs: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now() + ttlMs
    });
    this.ttlMap.set(key, ttlMs); // Store the TTL for this key
  }

  delete(key: string): boolean {
    this.ttlMap.delete(key);
    return this.cache.delete(key);
  }

  // Method to clear cache, optionally keeping certain keys
  clear(keepKeys: string[] = []): void {
      const keysToDelete = Array.from(this.cache.keys()).filter(key => !keepKeys.includes(key));
      keysToDelete.forEach(key => this.delete(key));
  }

  // Optional: Method to force refresh by clearing specific keys
  invalidate(...keys: string[]): void {
      keys.forEach(key => this.delete(key));
  }
}

const dataCache = new SimpleCache();

const CACHE_DURATIONS = {
  MARKET_OVERVIEW: 5 * 60 * 1000, // 5 minutes
  DAILY: 4 * 60 * 60 * 1000, // 4 hours
  INTRADAY: 5 * 60 * 1000,   // 5 minutes
  QUOTE: 1 * 60 * 1000,     // 1 minute for quotes
};

export const marketDataService = {
  getTimeSeriesDaily: async (symbol: string, providerType?: 'alpha_vantage' | 'yahoo_finance' | 'alpaca') => {
    const cacheKey = `daily_${symbol}_${providerType || 'default'}`;
    const cachedData = dataCache.get(cacheKey);
    
    if (cachedData) return cachedData;
    
    const provider = providerType 
      ? ProviderFactory.getProvider(providerType)
      : ProviderFactory.getDefaultProvider();
    
    const data = await provider.getTimeSeriesDaily(symbol);
    dataCache.set(cacheKey, data, CACHE_DURATIONS.DAILY);
    
    return data;
  },
  
  getTimeSeriesIntraday: async (symbol: string, interval: string, providerType?: 'alpha_vantage' | 'yahoo_finance' | 'alpaca') => {
    const cacheKey = `intraday_${symbol}_${interval}_${providerType || 'default'}`;
    const cachedData = dataCache.get(cacheKey);
    
    if (cachedData) return cachedData;
    
    const provider = providerType 
      ? ProviderFactory.getProvider(providerType)
      : ProviderFactory.getDefaultProvider();
    
    const data = await provider.getTimeSeriesIntraday(symbol, interval);
    dataCache.set(cacheKey, data, CACHE_DURATIONS.INTRADAY);
    
    return data;
  },
  
  getQuote: async (symbol: string, providerType?: 'alpha_vantage' | 'yahoo_finance' | 'alpaca') => {
    const cacheKey = `quote_${symbol}_${providerType || 'default'}`;
    const cachedData = dataCache.get(cacheKey);
    
    if (cachedData && 'timestamp' in cachedData && (Date.now() - cachedData.timestamp) < 60000) {
      return cachedData.data;
    }
    
    const provider = providerType 
      ? ProviderFactory.getProvider(providerType)
      : ProviderFactory.getDefaultProvider();
    
    const data = await provider.getQuote(symbol);
    dataCache.set(cacheKey, { data, timestamp: Date.now() }, CACHE_DURATIONS.INTRADAY);
    
    return data;
  },
  
  // Fetch market indices using the default provider (Alpaca)
  getMarketIndices: async (): Promise<MarketIndex[]> => {
    const cacheKey = 'market_indices';
    const cachedData = dataCache.get(cacheKey);
    if (cachedData) return cachedData;

    // List of core market indices to fetch
    const DEFAULT_INDICES = [
      { name: 'S&P 500', symbol: 'SPY' },
      { name: 'Nasdaq', symbol: 'QQQ' },
      { name: 'Dow Jones', symbol: 'DIA' },
      { name: '10Y Treasury', symbol: 'IEF' }
    ];
    const provider = ProviderFactory.getDefaultProvider();
    const results: MarketIndex[] = [];

    for (const idx of DEFAULT_INDICES) {
      try {
        const quote: any = await provider.getQuote(idx.symbol);
        results.push({
          name: idx.name,
          symbol: idx.symbol,
          value: quote.price,
          changePercent: quote.changePercent ?? 0
        });
      } catch (err) {
        console.warn(`Failed to fetch ${idx.symbol} from provider:`, err);
      }
    }
    
    if (results.length === 0) {
      // Fallback hardcoded data
      const fallback: MarketIndex[] = [
        { name: 'S&P 500', symbol: 'SPY', value: 5173.44, changePercent: 0.53 },
        { name: 'Nasdaq', symbol: 'QQQ', value: 16408.92, changePercent: 0.51 },
        { name: 'Dow Jones', symbol: 'DIA', value: 38239.97, changePercent: 0.16 },
        { name: '10Y Treasury', symbol: 'IEF', value: 4.58, changePercent: 0.44 }
      ];
      dataCache.set(cacheKey, fallback, CACHE_DURATIONS.MARKET_OVERVIEW);
      return fallback;
    }

    dataCache.set(cacheKey, results, CACHE_DURATIONS.MARKET_OVERVIEW);
    return results;
  },
  
  getSectorPerformance: async (): Promise<MarketSector[]> => {
    const cacheKey = 'market_sectors';
    const cachedData = dataCache.get(cacheKey);
    
    if (cachedData) return cachedData;
    
    try {
      // Using market data from integration service fallback
      const sectors: MarketSector[] = [
        { name: 'Technology', performance: 1.8 },
        { name: 'Healthcare', performance: -0.5 },
        { name: 'Financials', performance: 0.9 },
        { name: 'Consumer Discretionary', performance: 0.3 },
        { name: 'Industrials', performance: 1.2 },
        { name: 'Energy', performance: -1.1 },
        { name: 'Communication Services', performance: 0.7 },
        { name: 'Materials', performance: -0.2 },
        { name: 'Utilities', performance: -0.4 },
        { name: 'Real Estate', performance: 0.1 },
        { name: 'Consumer Staples', performance: 0.5 }
      ];
      
      // Cache the sectors data
      dataCache.set(cacheKey, sectors, CACHE_DURATIONS.MARKET_OVERVIEW);
      
      return sectors;
    } catch (error) {
      console.error('Error fetching sector performance:', error);
      
      // Return hardcoded fallback data
      return [
        { name: 'Technology', performance: 1.8 },
        { name: 'Healthcare', performance: -0.5 },
        { name: 'Financials', performance: 0.9 },
        { name: 'Consumer Discretionary', performance: 0.3 },
        { name: 'Industrials', performance: 1.2 },
        { name: 'Energy', performance: -1.1 },
        { name: 'Communication Services', performance: 0.7 },
        { name: 'Materials', performance: -0.2 },
        { name: 'Utilities', performance: -0.4 },
        { name: 'Real Estate', performance: 0.1 },
        { name: 'Consumer Staples', performance: 0.5 }
      ];
    }
  },
  invalidateCache: (...keys: string[]) => {
      keys.forEach(key => dataCache.delete(key));
  },

  clearAllCache: () => {
      dataCache.clear();
  }
};
